"use client";

import { useEffect, useState } from "react";
import {
  Button,
  Card,
  IntentBadge,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SettingsSection,
  Switch,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import {
  modelsConfig,
  type DataPolicy,
  type ModelsConfigResponse,
  type ProviderView,
} from "@excellent/ui/api/contracts/models-config";
import {
  modelsDiscover as discoverContract,
  type ModelsLocalStatusResponse,
} from "@excellent/ui/api/contracts/models-discover";

// One-line retention disclosure per data policy (§11).
const POLICY_COPY: Record<DataPolicy, string> = {
  zdr: "Zero data retention — prompts and completions are never stored by the provider.",
  "no-collection": "No training use — your data isn't used to train models (may be briefly retained for abuse monitoring).",
  default: "Provider default — requests may be logged and retained per the provider's standard policy.",
};

function KeyStatus({ p }: { p: ProviderView }) {
  if (p.keySource === "local") return <IntentBadge intent="info">Local — no key</IntentBadge>;
  if (p.keySource === "env") return <IntentBadge intent="success">Key detected</IntentBadge>;
  if (p.keySource === "sealed") return <IntentBadge intent="success">Sealed key</IntentBadge>;
  return <IntentBadge intent="warning">Set {p.keyEnvName}</IntentBadge>;
}

export function ProvidersSection({
  data,
  onChange,
}: {
  data: ModelsConfigResponse;
  onChange: () => void;
}) {
  const [openRouterOpen, setOpenRouterOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [ollamaBusy, setOllamaBusy] = useState(false);
  const [localOpen, setLocalOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  async function setPolicy(id: string, dataPolicy: DataPolicy) {
    await apiCall(modelsConfig.setDataPolicy, { body: { id, dataPolicy } });
    onChange();
  }

  async function useLocalOllama() {
    setOllamaBusy(true);
    setNotice(null);
    try {
      const disc = await apiCall(discoverContract.get, {});
      const found = disc.providers.find((d) => d.id.startsWith("ollama")) ?? disc.providers[0];
      const model = found?.models[0] ?? "qwen2.5-coder:7b";
      const res = await apiCall(modelsConfig.saveProvider, {
        body: { id: "ollama", setSingleShot: true, model, dataPolicy: "no-collection" },
      });
      setNotice(
        found
          ? `Connected local Ollama (${model}) — runs entirely on your machine.`
          : `Configured Ollama on :11434. Not detected yet — start Ollama, then run a checkup. (${res.keyEnvName} not needed for local.)`,
      );
      onChange();
    } catch (e) {
      setNotice(`Couldn't reach discovery: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setOllamaBusy(false);
    }
  }

  return (
    <SettingsSection
      title="Providers"
      description="Built-in providers plus anything you add. Local runtimes need no key; remote providers read their key from an environment variable — never from config."
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpenRouterOpen(true)}>
            Add OpenRouter
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCustomOpen(true)}>
            Add custom endpoint
          </Button>
          <Button variant="ghost" size="sm" disabled={ollamaBusy} onClick={useLocalOllama}>
            {ollamaBusy ? "Detecting…" : "Use local Ollama"}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setLocalOpen(true)}>
            Manage local models
          </Button>
        </div>
      }
    >
      <Card>
        <ul>
          {data.providers.map((p) => (
            <li
              key={p.id}
              className="flex flex-wrap items-center gap-3 border-b border-border/40 py-3 type-body first:pt-0 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-foreground">{p.label}</span>
                  <span className="type-subtext text-muted-foreground">· {p.transport}</span>
                  {p.singleShot ? <IntentBadge intent="success">Single-shot default</IntentBadge> : null}
                </div>
                <div className="truncate type-subtext text-muted-foreground">
                  {p.baseUrl ?? "spawned CLI"}
                  {p.jurisdiction ? ` · ${p.jurisdiction}` : ""}
                </div>
              </div>
              <KeyStatus p={p} />
              {!p.local ? (
                <div className="flex flex-col items-end gap-1">
                  <Select value={p.dataPolicy} onValueChange={(v) => setPolicy(p.id, v as DataPolicy)}>
                    <SelectTrigger className="w-44" aria-label={`Data policy for ${p.label}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="zdr">Zero data retention</SelectItem>
                      <SelectItem value="no-collection">No training use</SelectItem>
                      <SelectItem value="default">Provider default</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
        {data.providers.some((p) => !p.local) ? (
          <p className="mt-3 type-subtext text-muted-foreground">
            {POLICY_COPY[
              (data.providers.find((p) => !p.local)?.dataPolicy ?? "default") as DataPolicy
            ]}
          </p>
        ) : null}
      </Card>

      {notice ? <p className="mt-3 type-subtext text-muted-foreground">{notice}</p> : null}

      <ConnectOpenRouterDialog
        open={openRouterOpen}
        onOpenChange={setOpenRouterOpen}
        onConnected={(msg) => {
          setNotice(msg);
          onChange();
        }}
      />
      <ConnectCustomDialog
        open={customOpen}
        onOpenChange={setCustomOpen}
        onConnected={(msg) => {
          setNotice(msg);
          onChange();
        }}
      />
      <LocalModelsDialog open={localOpen} onOpenChange={setLocalOpen} />
    </SettingsSection>
  );
}

/** Approx bytes → "4.7GB" for the tier-picker (download sizes, not money/dates). */
function gb(n: number): string {
  return `${(n / 1024 ** 3).toFixed(1)}GB`;
}

// Native local-model lifecycle (LM-A): tier-picker + Install/Remove over the
// /api/models/local contract. Downloads are consent-audited server-side
// (activities verbs models.local-pull / models.local-remove); the residency
// policy (single-resident eviction, disk budget) is enforced in the pull path.
function LocalModelsDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const [status, setStatus] = useState<ModelsLocalStatusResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function refresh() {
    try {
      setStatus(await apiCall(discoverContract.local, {}));
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => {
    if (open) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function install(model: string) {
    setBusy(model);
    setNotice(null);
    try {
      const res = await apiCall(discoverContract.install, { body: { model } });
      setNotice(
        res.ok
          ? `Installed ${model}${res.evicted?.length ? ` — evicted ${res.evicted.join(", ")} (single-resident policy)` : ""}.`
          : res.reason ?? `Install of ${model} refused.`,
      );
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove(model: string) {
    setBusy(model);
    setNotice(null);
    try {
      const res = await apiCall(discoverContract.remove, { body: { model } });
      setNotice(res.ok ? `Removed ${model}.` : res.reason ?? `Remove of ${model} failed.`);
      await refresh();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const residentNames = new Set(
    (status?.resident ?? []).map((r) => r.name.replace(/:latest$/, "")),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Local models</DialogTitle>
          <DialogDescription>
            {status
              ? status.reachable
                ? `Ollama is running · ${gb(status.headroomBytes)} free disk${status.residency.singleResident ? " · single-resident policy on" : ""}`
                : "Ollama isn't reachable — start it (`ollama serve` or the desktop app). Excellent never auto-installs a runtime."
              : "Checking the local runtime…"}
          </DialogDescription>
        </DialogHeader>
        <ul>
          {(status?.catalog ?? []).map((t) => {
            const installed = residentNames.has(t.model);
            return (
              <li
                key={t.model}
                className="flex items-center gap-3 border-b border-border/40 py-2 type-body last:border-b-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-foreground">{t.model}</div>
                  <div className="type-subtext text-muted-foreground">
                    {t.paramClass} · tier {t.tier} · ~{gb(t.approxBytes)}
                  </div>
                </div>
                {installed ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={busy !== null || !status?.reachable}
                    onClick={() => remove(t.model)}
                  >
                    {busy === t.model ? "Working…" : "Remove"}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busy !== null || !status?.reachable}
                    onClick={() => install(t.model)}
                  >
                    {busy === t.model ? "Downloading…" : "Install"}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
        {notice ? <p className="type-subtext text-muted-foreground">{notice}</p> : null}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConnectOpenRouterDialog({
  open,
  onOpenChange,
  onConnected,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConnected: (msg: string) => void;
}) {
  const [model, setModel] = useState("anthropic/claude-3.5-haiku");
  const [asDefault, setAsDefault] = useState(true);
  const [busy, setBusy] = useState(false);

  async function connect() {
    setBusy(true);
    try {
      const res = await apiCall(modelsConfig.saveProvider, {
        body: {
          id: "openrouter",
          dataPolicy: "zdr",
          setSingleShot: asDefault && !!model.trim(),
          model: model.trim() || undefined,
        },
      });
      onOpenChange(false);
      onConnected(
        res.keyPresent
          ? `OpenRouter connected — key detected in ${res.keyEnvName}.`
          : `OpenRouter configured. Set your key in ${res.keyEnvName} (Excellent never stores keys in config), then restart.`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add OpenRouter</DialogTitle>
          <DialogDescription>
            OpenRouter reads its key from the <code>OPENROUTER_API_KEY</code>{" "}
            environment variable. Excellent never writes model keys to its config —
            set the env var (or <code>~/.excellent/sync.env</code>) and Excellent
            resolves it at call time.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="block type-body">
            <span className="text-foreground">Default model</span>
            <Input
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="anthropic/claude-3.5-haiku"
              className="mt-1"
            />
          </label>
          <label className="flex items-center justify-between type-body">
            <span className="text-foreground">Use as the single-shot default</span>
            <Switch checked={asDefault} onCheckedChange={setAsDefault} aria-label="Use as single-shot default" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={connect}>
            {busy ? "Connecting…" : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConnectCustomDialog({
  open,
  onOpenChange,
  onConnected,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onConnected: (msg: string) => void;
}) {
  const [id, setId] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [model, setModel] = useState("");
  const [jurisdiction, setJurisdiction] = useState("");
  const [dataPolicy, setDataPolicy] = useState<DataPolicy>("default");
  const [busy, setBusy] = useState(false);

  const valid = id.trim().length > 0 && /^https?:\/\//.test(baseUrl.trim());

  async function connect() {
    setBusy(true);
    try {
      const res = await apiCall(modelsConfig.saveProvider, {
        body: {
          id: id.trim(),
          transport: "openai-http",
          baseUrl: baseUrl.trim(),
          dataPolicy,
          jurisdiction: jurisdiction.trim() || undefined,
          setSingleShot: !!model.trim(),
          model: model.trim() || undefined,
        },
      });
      onOpenChange(false);
      onConnected(
        res.keyPresent
          ? `${id.trim()} connected — key detected in ${res.keyEnvName}.`
          : `${id.trim()} configured. Set its key in ${res.keyEnvName} (never stored in config), then restart.`,
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a custom endpoint</DialogTitle>
          <DialogDescription>
            Any OpenAI-compatible endpoint. Its key is read from an environment
            variable, never stored in config. Add a jurisdiction line so anyone
            reviewing residency knows where requests go (§11).
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <label className="block type-body">
            <span className="text-foreground">Provider id</span>
            <Input value={id} onChange={(e) => setId(e.target.value)} placeholder="my-endpoint" className="mt-1" />
          </label>
          <label className="block type-body">
            <span className="text-foreground">Base URL</span>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://api.example.com/v1" className="mt-1" />
          </label>
          <label className="block type-body">
            <span className="text-foreground">Default model <span className="text-muted-foreground">(optional)</span></span>
            <Input value={model} onChange={(e) => setModel(e.target.value)} placeholder="my-model-id" className="mt-1" />
          </label>
          <label className="block type-body">
            <span className="text-foreground">Jurisdiction disclosure <span className="text-muted-foreground">(optional)</span></span>
            <Input value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} placeholder="Requests egress to us-east-1 (AWS)" className="mt-1" />
          </label>
          <label className="block type-body">
            <span className="text-foreground">Data policy</span>
            <Select value={dataPolicy} onValueChange={(v) => setDataPolicy(v as DataPolicy)}>
              <SelectTrigger className="mt-1 w-full" aria-label="Data policy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="zdr">Zero data retention</SelectItem>
                <SelectItem value="no-collection">No training use</SelectItem>
                <SelectItem value="default">Provider default</SelectItem>
              </SelectContent>
            </Select>
          </label>
          <p className="type-subtext text-muted-foreground">{POLICY_COPY[dataPolicy]}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || !valid} onClick={connect}>
            {busy ? "Connecting…" : "Connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
